import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { BACKEND_NODE_URL } from '../config/constants';
import AppIcon from './icons/AppIcon';

const CampaignModal = ({ campaign, onClose, onSave }) => {
  const { user } = useAuth();

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [projectId, setProjectId] = useState('');
  const [sourceListId, setSourceListId] = useState('');
  const [connectionId, setConnectionId] = useState('');
  const [aiAgentId, setAiAgentId] = useState('');

  // Message config
  const [messageType, setMessageType] = useState('manual'); // manual | agent
  const [contentType, setContentType] = useState('text'); // text | image | audio | audio | document
  const [messageContent, setMessageContent] = useState('');
  const [mediaUrl, setMediaUrl] = useState('');
  const [agentPrompt, setAgentPrompt] = useState('');

  // Scheduling
  const [dailyStartTime, setDailyStartTime] = useState('08:00');
  const [dailyEndTime, setDailyEndTime] = useState('18:00');
  const [autoFinish, setAutoFinish] = useState(true);

  // API Configuration (para /sender/simple)
  const [delayMin, setDelayMin] = useState(10);
  const [delayMax, setDelayMax] = useState(30);
  const [scheduledFor, setScheduledFor] = useState(Date.now());
  const [messageTypeApi, setMessageTypeApi] = useState('text'); // text, image, video, audio, document
  const [linkPreview, setLinkPreview] = useState(false);

  // Data
  const [projects, setProjects] = useState([]);
  const [agents, setAgents] = useState([]);
  const [connections, setConnections] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadingData, setLoadingData] = useState(true);

  const getAuthHeaders = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session?.access_token}`
    };
  }, []);

  const [selectedListData, setSelectedListData] = useState(null);
  const [contactsPage, setContactsPage] = useState(0);
  const CONTACTS_PER_PAGE = 10;

  // Carregar dados da lista de marketing selecionada (se houver)
  useEffect(() => {
    const selectedList = sessionStorage.getItem('selectedMarketingList');
    if (selectedList && !campaign) {
      try {
        const list = JSON.parse(selectedList);
        console.log('[CampaignModal] Pré-preenchendo com lista:', list);
        
        // Armazenar dados da lista
        setSelectedListData(list);
        
        // Pré-preencher campos com dados da lista
        setName(list.list_name);
        setProjectId(list.project_id);
        setSourceListId(list.id);
        setConnectionId(list.connection_id || '');
        
        // Limpar sessionStorage
        sessionStorage.removeItem('selectedMarketingList');
      } catch (error) {
        console.error('[CampaignModal] Erro ao carregar lista pré-selecionada:', error);
      }
    }
  }, [campaign]);

  // Extrair contatos da lista selecionada
  const getContactsFromList = () => {
    if (!selectedListData?.contacts_data) return [];
    const contactsData = selectedListData.contacts_data;
    if (Array.isArray(contactsData)) return contactsData;
    if (contactsData.contacts && Array.isArray(contactsData.contacts)) return contactsData.contacts;
    return [];
  };

  const allContacts = getContactsFromList();
  const paginatedContacts = allContacts.slice(
    contactsPage * CONTACTS_PER_PAGE,
    (contactsPage + 1) * CONTACTS_PER_PAGE
  );
  const totalPages = Math.ceil(allContacts.length / CONTACTS_PER_PAGE);

  // Carregar projetos CRM, conexões e agentes
  useEffect(() => {
    const loadData = async () => {
      if (!user?.company_id) return;
      try {
        setLoadingData(true);

        // Carregar projetos CRM (RLS vai filtrar por company_id)
        const { data: projectsData } = await supabase
          .from('projects')
          .select('id, name, project_type, ai_agent_id, settings')
          .eq('is_archived', false)
          .order('name');

        setProjects(projectsData || []);

        // Carregar conexões (RLS vai filtrar por company_id)
        const { data: connectionsData } = await supabase
          .from('conexoes_uazapi')
          .select('id, nome, numero, status')
          .order('nome');

        setConnections(connectionsData || []);

        // Carregar agentes (RLS vai filtrar por company_id)
        const { data: agentsData } = await supabase
          .from('agentes')
          .select('id, nome, funcao')
          .order('nome');

        setAgents(agentsData || []);
      } catch (error) {
        console.error('[CampaignModal] Erro ao carregar dados:', error);
      } finally {
        setLoadingData(false);
      }
    };

    loadData();
  }, [user?.company_id]);

  // Não carrega listas - usa a lista pré-selecionada da marketing_contact_lists

  // Quando selecionar projeto, carregar conexão e agente automaticamente
  useEffect(() => {
    if (!projectId) return;
    const project = projects.find(p => p.id === projectId);
    if (project) {
      // Se o projeto tem agente, setar automaticamente
      if (project.ai_agent_id) {
        setAiAgentId(project.ai_agent_id);
      }
      // Se o projeto tem conexão no settings, setar
      if (project.settings?.connection_id) {
        setConnectionId(project.settings.connection_id);
      }
    }
  }, [projectId, projects]);

  // Preencher form se editando
  useEffect(() => {
    if (campaign) {
      setName(campaign.name || '');
      setDescription(campaign.description || '');
      setProjectId(campaign.project_id || '');
      setSourceListId(campaign.source_list_id || '');
      setConnectionId(campaign.connection_id || '');
      setAiAgentId(campaign.ai_agent_id || '');

      const mc = campaign.message_config || {};
      setMessageType(mc.message_type || 'manual');
      setContentType(mc.content_type || 'text');
      setMessageContent(mc.content || '');
      setMediaUrl(mc.media_url || '');
      setAgentPrompt(mc.prompt || '');
      setLinkPreview(mc.linkPreview || false);
      setMessageTypeApi(mc.type || 'text');

      const sched = campaign.scheduling || {};
      setDailyStartTime(sched.daily_start_time || '08:00');
      setDailyEndTime(sched.daily_end_time || '18:00');
      setAutoFinish(sched.auto_finish_when_list_ends !== false);

      // Carregar delay_min e delay_max
      setDelayMin(campaign.delay_min || 10);
      setDelayMax(campaign.delay_max || 30);
    }
  }, [campaign]);

  const handleSave = async () => {
    if (!name.trim()) {
      alert('Nome da campanha é obrigatório');
      return;
    }
    if (!projectId) {
      alert('Selecione um projeto CRM');
      return;
    }
    if (!sourceListId) {
      alert('Selecione uma lista de origem');
      return;
    }
    if (messageType === 'manual' && !messageContent.trim()) {
      alert('Preencha o conteúdo da mensagem');
      return;
    }
    if (messageType === 'agent' && !agentPrompt.trim()) {
      alert('Preencha a instrução para o agente');
      return;
    }

    try {
      setSaving(true);
      const headers = await getAuthHeaders();

      const messageConfig = {
        type: messageTypeApi,
        message_type: messageType,
        ...(messageType === 'manual' ? {
          content_type: contentType,
          content: messageContent.trim(),
          media_url: mediaUrl.trim() || null,
          linkPreview: linkPreview
        } : {
          prompt: agentPrompt.trim(),
          agent_id: aiAgentId || null,
          linkPreview: false
        }),
        // Configurações da API de envio
        delayMin,
        delayMax
      };

      const scheduling = {
        daily_start_time: dailyStartTime,
        daily_end_time: dailyEndTime,
        auto_finish_when_list_ends: autoFinish,
        paused: false
      };

      const body = {
        name: name.trim(),
        description: description.trim() || null,
        project_id: projectId,
        source_list_id: sourceListId,
        connection_id: connectionId || null,
        ai_agent_id: aiAgentId || null,
        message_config: messageConfig,
        scheduling,
        delay_min: delayMin,
        delay_max: delayMax
      };

      let response;
      if (campaign?.id) {
        response = await fetch(`${BACKEND_NODE_URL}/api/campaigns/${campaign.id}`, {
          method: 'PUT', headers, body: JSON.stringify(body)
        });
      } else {
        response = await fetch(`${BACKEND_NODE_URL}/api/campaigns`, {
          method: 'POST', headers, body: JSON.stringify(body)
        });
      }

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Erro ao salvar');

      onSave();
    } catch (error) {
      alert('Erro ao salvar campanha: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const isReadOnly = campaign?.status === 'completed';

  return (
    <div className="modal-overlay" onClick={onClose} style={overlayStyle}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={modalStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <h3 style={headerTitleStyle}>
            <AppIcon name="campaign" style={{ color: '#b43bea' }} />
            {campaign ? (isReadOnly ? 'Detalhes da Campanha' : 'Editar Campanha') : 'Nova Campanha'}
          </h3>
          <button onClick={onClose} style={closeBtnStyle}>
            <AppIcon name="close" />
          </button>
        </div>

        {/* Body */}
        <div style={bodyStyle}>
          {loadingData ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#9bb3c7' }}>
              Carregando dados...
            </div>
          ) : (
            <>
              {/* Nome e Descrição */}
              <div style={sectionStyle}>
                <label style={labelStyle}>Nome da Campanha *</label>
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Ex: Remarketing Clientes Inativos"
                  style={inputStyle}
                  disabled={isReadOnly}
                />

                <label style={{ ...labelStyle, marginTop: '0.8rem' }}>Descrição</label>
                <textarea
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="Descrição da campanha..."
                  rows={2}
                  style={{ ...inputStyle, resize: 'vertical' }}
                  disabled={isReadOnly}
                />
              </div>

              {/* Projeto e Lista */}
              <div style={sectionStyle}>
                <div style={sectionHeaderStyle}>
                  <AppIcon name="dashboard" style={{ fontSize: '1rem', color: '#b43bea' }} />
                  <span style={sectionTitleStyle}>Projeto e Lista</span>
                </div>

                {/* Informações da Lista de Campanha Pré-preenchidas */}
                {selectedListData && (
                  <>
                    <div style={{ ...inputStyle, padding: '1rem', background: 'rgba(76, 175, 80, 0.1)', border: '1px solid rgba(76, 175, 80, 0.3)', marginBottom: '0.8rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <AppIcon name="check_circle" style={{ color: '#4caf50', fontSize: '1.2rem' }} />
                        <span style={{ color: '#4caf50', fontWeight: 600 }}>Lista de Campanha Selecionada</span>
                      </div>
                      <div style={{ fontSize: '0.9rem', color: 'var(--text-color)' }}>
                        <div style={{ marginBottom: '0.3rem' }}>
                          <strong>Nome:</strong> {selectedListData.list_name}
                        </div>
                        <div style={{ marginBottom: '0.3rem' }}>
                          <strong>Contatos:</strong> {selectedListData.total_contacts || 0}
                        </div>
                        {selectedListData.connection_id && (
                          <div>
                            <strong>Conexão:</strong> {connections.find(c => c.id === selectedListData.connection_id)?.nome || 'Configurada'}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Lista de Contatos com Paginação */}
                    {allContacts.length > 0 && (
                      <div style={{ ...inputStyle, padding: '1rem', background: 'rgba(68, 225, 255, 0.05)', border: '1px solid rgba(68, 225, 255, 0.2)', marginBottom: '0.8rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.8rem' }}>
                          <AppIcon name="people" style={{ color: '#44e1ff', fontSize: '1.2rem' }} />
                          <span style={{ color: '#44e1ff', fontWeight: 600 }}>Contatos ({allContacts.length})</span>
                        </div>
                        
                        {/* Tabela de Contatos */}
                        <div style={{ maxHeight: '300px', overflowY: 'auto', marginBottom: '0.8rem' }}>
                          <table style={{ width: '100%', fontSize: '0.85rem', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr style={{ borderBottom: '1px solid rgba(68, 225, 255, 0.2)' }}>
                                <th style={{ textAlign: 'left', padding: '0.5rem', color: '#44e1ff' }}>Nome</th>
                                <th style={{ textAlign: 'left', padding: '0.5rem', color: '#44e1ff' }}>Telefone</th>
                              </tr>
                            </thead>
                            <tbody>
                              {paginatedContacts.map((contact, idx) => (
                                <tr key={idx} style={{ borderBottom: '1px solid rgba(68, 225, 255, 0.1)' }}>
                                  <td style={{ padding: '0.5rem', color: 'var(--text-color)' }}>
                                    {contact.contact_name || contact.name || contact.nome || '-'}
                                  </td>
                                  <td style={{ padding: '0.5rem', color: 'var(--text-color)' }}>
                                    {contact.numero || contact.phone || '-'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {/* Paginação */}
                        {totalPages > 1 && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.8rem', color: 'var(--text-color-secondary)' }}>
                            <span>Página {contactsPage + 1} de {totalPages}</span>
                            <div style={{ display: 'flex', gap: '0.4rem' }}>
                              <button
                                type="button"
                                onClick={() => setContactsPage(Math.max(0, contactsPage - 1))}
                                disabled={contactsPage === 0}
                                style={{ ...inputStyle, padding: '0.4rem 0.8rem', fontSize: '0.8rem', cursor: contactsPage === 0 ? 'not-allowed' : 'pointer', opacity: contactsPage === 0 ? 0.5 : 1 }}
                              >
                                ← Anterior
                              </button>
                              <button
                                type="button"
                                onClick={() => setContactsPage(Math.min(totalPages - 1, contactsPage + 1))}
                                disabled={contactsPage === totalPages - 1}
                                style={{ ...inputStyle, padding: '0.4rem 0.8rem', fontSize: '0.8rem', cursor: contactsPage === totalPages - 1 ? 'not-allowed' : 'pointer', opacity: contactsPage === totalPages - 1 ? 0.5 : 1 }}
                              >
                                Próxima →
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}

              </div>

              {/* Tipo de Mensagem */}
              <div style={sectionStyle}>
                <div style={sectionHeaderStyle}>
                  <AppIcon name="message" style={{ fontSize: '1rem', color: '#b43bea' }} />
                  <span style={sectionTitleStyle}>Mensagem</span>
                </div>

                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
                  <button
                    type="button"
                    onClick={() => setMessageType('manual')}
                    style={messageType === 'manual' ? tabActiveBtnStyle : tabBtnStyle}
                    disabled={isReadOnly}
                  >
                    <AppIcon name="edit" style={{ fontSize: '1rem' }} />
                    Manual
                  </button>
                  <button
                    type="button"
                    onClick={() => setMessageType('agent')}
                    style={messageType === 'agent' ? tabActiveBtnStyle : tabBtnStyle}
                    disabled={isReadOnly}
                  >
                    <AppIcon name="smart_toy" style={{ fontSize: '1rem' }} />
                    Agente IA
                  </button>
                </div>

                {messageType === 'manual' ? (
                  <>
                    <label style={labelStyle}>Mensagem *</label>
                    <textarea
                      value={messageContent}
                      onChange={e => setMessageContent(e.target.value)}
                      placeholder="Digite a mensagem que será enviada..."
                      rows={4}
                      style={{ ...inputStyle, resize: 'vertical' }}
                      disabled={isReadOnly}
                    />
                  </>
                ) : (
                  <>
                    <label style={labelStyle}>Agente</label>
                    <select
                      value={aiAgentId}
                      onChange={e => setAiAgentId(e.target.value)}
                      style={inputStyle}
                      disabled={isReadOnly}
                    >
                      <option value="">Automático (do projeto)</option>
                      {agents.map(a => (
                        <option key={a.id} value={a.id}>{a.nome} ({a.funcao || 'Geral'})</option>
                      ))}
                    </select>

                    <label style={{ ...labelStyle, marginTop: '0.8rem' }}>
                      Instrução para o Agente *
                    </label>
                    <textarea
                      value={agentPrompt}
                      onChange={e => setAgentPrompt(e.target.value)}
                      placeholder="Ex: Crie uma mensagem amigável de remarketing para clientes inativos, oferecendo 20% de desconto..."
                      rows={5}
                      style={{ ...inputStyle, resize: 'vertical' }}
                      disabled={isReadOnly}
                    />
                    <p style={{ fontSize: '0.72rem', color: '#9bb3c7', margin: '0.3rem 0 0' }}>
                      O agente vai gerar uma mensagem personalizada para cada contato usando esta instrução.
                    </p>
                  </>
                )}
              </div>

              {/* Configurações */}
              <div style={sectionStyle}>
                <div style={sectionHeaderStyle}>
                  <AppIcon name="settings" style={{ fontSize: '1rem', color: '#b43bea' }} />
                  <span style={sectionTitleStyle}>Configurações de Envio</span>
                </div>

                <div style={{ display: 'flex', gap: '1rem' }}>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Delay Mínimo (segundos)</label>
                    <input
                      type="number"
                      value={delayMin}
                      onChange={e => setDelayMin(parseInt(e.target.value) || 0)}
                      min="0"
                      max="3600"
                      style={inputStyle}
                      disabled={isReadOnly}
                    />
                    <p style={{ fontSize: '0.7rem', color: '#9bb3c7', margin: '0.2rem 0 0' }}>
                      Tempo mínimo entre mensagens
                    </p>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Delay Máximo (segundos)</label>
                    <input
                      type="number"
                      value={delayMax}
                      onChange={e => setDelayMax(parseInt(e.target.value) || 0)}
                      min="0"
                      max="3600"
                      style={inputStyle}
                      disabled={isReadOnly}
                    />
                    <p style={{ fontSize: '0.7rem', color: '#9bb3c7', margin: '0.2rem 0 0' }}>
                      Tempo máximo entre mensagens
                    </p>
                  </div>
                </div>

                <label style={{ ...labelStyle, marginTop: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={linkPreview}
                    onChange={e => setLinkPreview(e.target.checked)}
                    style={{ accentColor: '#b43bea' }}
                    disabled={isReadOnly || messageType !== 'manual'}
                  />
                  Habilitar Link Preview
                </label>
                <p style={{ fontSize: '0.7rem', color: '#9bb3c7', margin: '0.2rem 0 0' }}>
                  Mostra visualização de links contidos na mensagem
                </p>
              </div>

              {/* Agendamento */}
              <div style={sectionStyle}>
                <div style={sectionHeaderStyle}>
                  <AppIcon name="schedule" style={{ fontSize: '1rem', color: '#b43bea' }} />
                  <span style={sectionTitleStyle}>Agendamento</span>
                </div>

                <div style={{ display: 'flex', gap: '1rem' }}>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Hora de Início</label>
                    <input
                      type="time"
                      value={dailyStartTime}
                      onChange={e => setDailyStartTime(e.target.value)}
                      style={inputStyle}
                      disabled={isReadOnly}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Hora de Término</label>
                    <input
                      type="time"
                      value={dailyEndTime}
                      onChange={e => setDailyEndTime(e.target.value)}
                      style={inputStyle}
                      disabled={isReadOnly}
                    />
                  </div>
                </div>

                <label style={{ ...labelStyle, marginTop: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={autoFinish}
                    onChange={e => setAutoFinish(e.target.checked)}
                    style={{ accentColor: '#b43bea' }}
                    disabled={isReadOnly}
                  />
                  Auto-finalizar quando a lista acabar
                </label>
              </div>

            </>
          )}
        </div>

        {/* Footer */}
        <div style={footerStyle}>
          <button onClick={onClose} style={cancelBtnStyle}>Cancelar</button>
          {!isReadOnly && (
            <button onClick={handleSave} disabled={saving} style={saveBtnStyle}>
              {saving ? 'Salvando...' : (campaign?.id ? 'Salvar Alterações' : 'Criar Campanha')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Estilos inline para o modal (seguindo o padrão visual do sistema)
const overlayStyle = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)', display: 'flex',
  alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '2rem', overflowY: 'auto'
};

const modalStyle = {
  background: 'rgba(15, 12, 41, 0.98)', borderRadius: '16px', width: '100%', maxWidth: '1100px',
  maxHeight: '90vh', display: 'flex', flexDirection: 'column',
  border: '1px solid rgba(180, 59, 234, 0.3)', boxShadow: '0 25px 50px rgba(0,0,0,0.5)',
  animation: 'modalSlideIn 0.3s ease'
};

const headerStyle = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
  padding: '2rem 2rem 1rem 2rem', borderBottom: '1px solid rgba(255,255,255,0.1)'
};

const headerTitleStyle = {
  margin: 0, fontSize: '1.5rem', fontWeight: 600, color: '#ffffff',
  display: 'flex', alignItems: 'center', gap: '0.8rem'
};

const closeBtnStyle = {
  background: 'none', border: 'none', color: 'var(--text-color-secondary)', cursor: 'pointer',
  padding: '0.3rem', borderRadius: '6px', display: 'flex', fontSize: '1.5rem'
};

const bodyStyle = {
  flex: 1, overflowY: 'auto', padding: '1.5rem',
  display: 'flex', flexDirection: 'column', gap: '0.8rem'
};

const sectionStyle = {
  background: 'var(--card-background)', border: '1px solid rgba(49, 117, 177, 0.3)',
  borderRadius: '10px', padding: '1.2rem', marginBottom: '0.3rem',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
};

const sectionHeaderStyle = {
  display: 'flex', alignItems: 'center', gap: '0.5rem',
  marginBottom: '0.8rem', paddingBottom: '0.5rem',
  borderBottom: '1px solid rgba(49, 117, 177, 0.3)'
};

const sectionTitleStyle = {
  fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-color)'
};

const labelStyle = {
  display: 'block', fontSize: '0.8rem', fontWeight: 500,
  color: 'var(--text-color-secondary)', marginBottom: '0.4rem'
};

const inputStyle = {
  width: '100%', padding: '0.8rem', fontSize: '0.9rem',
  background: 'rgba(15, 12, 41, 0.5)', border: '1px solid rgba(180, 59, 234, 0.3)',
  borderRadius: '8px', color: 'var(--text-color)', outline: 'none',
  boxSizing: 'border-box', fontFamily: 'inherit'
};

const tabBtnStyle = {
  display: 'flex', alignItems: 'center', gap: '0.4rem',
  padding: '0.5rem 1rem', fontSize: '0.8rem', fontWeight: 500,
  background: 'rgba(15, 12, 41, 0.5)', border: '1px solid rgba(180, 59, 234, 0.3)',
  color: '#ffffff', cursor: 'pointer', borderRadius: '6px',
  transition: 'all 0.2s ease'
};

const tabActiveBtnStyle = {
  ...tabBtnStyle,
  background: 'rgba(78, 84, 200, 0.2)', borderColor: 'var(--primary-color)',
  color: '#ffffff'
};

const footerStyle = {
  display: 'flex', justifyContent: 'flex-end', gap: '0.8rem',
  padding: '1.5rem 2rem', borderTop: '1px solid rgba(49, 117, 177, 0.3)'
};

const cancelBtnStyle = {
  padding: '0.8rem 1.5rem', fontSize: '0.9rem', fontWeight: 500,
  background: 'rgba(15, 12, 41, 0.5)', border: '1px solid rgba(180, 59, 234, 0.3)',
  borderRadius: '8px', color: 'var(--text-color-secondary)', cursor: 'pointer',
  transition: 'all 0.2s ease'
};

const saveBtnStyle = {
  padding: '0.8rem 1.8rem', fontSize: '0.9rem', fontWeight: 600,
  background: 'linear-gradient(135deg, var(--primary-color), var(--secondary-color))', border: 'none',
  borderRadius: '8px', color: '#fff', cursor: 'pointer', transition: 'all 0.3s ease',
  boxShadow: '0 4px 15px rgba(78, 84, 200, 0.3)'
};

export default CampaignModal;
